import React, { useEffect, useState } from "react";
import GuideSection from "../../components/guide/GuideSection";
import { documentsGuide } from "../../components/guide/guideContent";
import { Link, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { Button, ListGroup, Alert } from "react-bootstrap";
import { FaRegTimesCircle } from "react-icons/fa";
import FormalityHeader from "../../components/header/FormalityHeader";
import { MdOutlineUploadFile } from "react-icons/md";
import { FiFile } from "react-icons/fi";
import { LiaFilePdf } from "react-icons/lia";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/loader/Loader";
import {
  eneoDocuments,
  uploadBusinessPlanDocuments,
  uploadCertificateNationalityDocuments,
  uploadCertificateSinglenessDocuments,
  uploadCrimRecordDocuments,
  uploadCustomsRebateDocuments,
  uploadImportationDocuments,
  uploadOapiDocuments,
  uploadCnpsRegistrationDocuments,
  uploadTaxRegistrationDocuments,
  uploadTrademarkDocuments,
} from "../../redux/actions/FormalityActions";
import "../styles/flow-form.css";

const documentTypes = {
  id: "La copie scannée de votre pièce d’identité ou de votre acte de naissance",
  witness1Id: "La photocopie de la carte nationale d’identité d'un témoin",
  witness2Id:
    "La copie scannée de la carte nationale d’identité d'un autre témoin",
  letter: "Demande manuscrite.",
  uin: "NUI Numéro d'identification unique (Attestation)",
  location: "Plan de localisation",
  neighborBill: "Facture du voisin",
  equipment: "Liste des équipements",
  invoice: "Facture commerciale",
  ticket: "Connaissement ou titre de transport",
  originCert: "Certificat d'origine",
  natureCert:
    "Certificat attestant la nature spécifique du bien (ex: certificat électrique pour un vehicule)",
  extra: "Tout document exigé selon le type de produit",
  projectDescription: "Description du projet",
  companyRegCert: "copie scannée Registre de Commerce certifié",
  noRoyaltyCert:
    "Copie  scannée de l'Attestation d'Exoneration à la Patente certifiée",
  taxPayerCard: "Copie scannée Attestation d'immatriculation",
  nc501: "Copie scannée du formulaire NC 501 dûment rempli",
  rccm: "La copie scannée du registre de commerce",
  managerId: "La copie scannée de la pièce d'identité du représentant",
  idCard: "La copie scannée de votre pièce d'identité",
  logo: "Le logo à protéger (de préférence avec le nom de la marque dessus)",
  taxRegistrationCert: "L'attestation d'immatriculation",
  taxComplianceCert: "L'attestation de conformité fiscale",
  locationPlan: "Le plan de localisation",
  employeesList: "La liste des employés",
};

// File types accepted for each document, PDF only by default
const PDF = { "application/pdf": [".pdf"] };
const IMAGES = { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"] };
const acceptedTypes = {
  logo: { ...PDF, ...IMAGES },
  employeesList: {
    ...PDF,
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
  },
};
const acceptedLabel = {
  logo: "PDF, PNG ou JPG",
  employeesList: "PDF ou Excel",
};

const documentsByFormality = {
  crimRecord: ["id"],
  certificateNationality: ["id"],
  certificateSingleness: ["id", "witness1Id", "witness2Id"],
  eneo: ["id", "letter", "uin", "location", "neighborBill", "equipment"],
  customsRebate: ["invoice", "ticket", "originCert", "natureCert"],
  businessPlan: ["projectDescription"],
  importation: ["companyRegCert", "noRoyaltyCert", "taxPayerCard"],
  oapi: ["rccm", "managerId"],
  trademark: ["idCard", "logo"],
  // A company also provides its trade register
  taxRegistration: (request) =>
    request?.holderType === "company" ? ["idCard", "rccm"] : ["idCard"],
  cnpsRegistration: [
    "rccm",
    "taxRegistrationCert",
    "taxComplianceCert",
    "locationPlan",
    "employeesList",
  ],
};

function FormalityDocuments() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const cart = useSelector((state) => state.cart);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentDocument, setCurrentDocument] = useState("");
  const [animate, setAnimate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const { formalityRequest } = useSelector((state) => state.formalityRequest);

  useEffect(() => {
    // Some lists depend on the answers given in the request
    const expected =
      typeof documentsByFormality[cart.from] === "function"
        ? documentsByFormality[cart.from](formalityRequest)
        : documentsByFormality[cart.from];

    if (cart.from && expected) {
      let docs = [...expected];

      setRequiredDocuments(docs);
      setUploadedFiles([]);
      setCurrentDocument(docs[0] || "");
    }
  }, [cart.from, formalityRequest]);

  // Fonction pour gérer les fichiers déposés
  const onDrop = (acceptedFiles) => {
    if (!currentDocument) return;

    const newFile = acceptedFiles[0];

    const content = {
      file: newFile,
      name: newFile.name,
      id: Math.random().toString(36).substring(2, 9),
      documentType: currentDocument,
      uri: newFile.path || newFile.uri,
      type: newFile.type,
    };

    setUploadedFiles((prevFiles) => [...prevFiles, content]);

    Swal.fire({
      icon: "success",
      title: "Succès",
      text: `${documentTypes[currentDocument]} ajouté.`,
      toast: true,
      position: "top-right",
      showConfirmButton: false,
      timer: 2000,
    });

    const remainingDocs = requiredDocuments.filter(
      (doc) => !uploadedFiles.some((file) => file.documentType === doc),
    );

    if (remainingDocs.length > 1) {
      setCurrentDocument(remainingDocs[1]);
      setAnimate(true);
      setTimeout(() => setAnimate(false), 1000);
    } else {
      setCurrentDocument("");
    }
  };

  // Configuration de Dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes[currentDocument] || PDF,
    multiple: false, // Autorise plusieurs fichiers
  });

  // Fonction pour supprimer un fichier
  const removeFile = (id, documentType) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.id !== id));
    setCurrentDocument(documentType);
  };

  const handleButtonClick = async () => {
    setSubmitting(true);

    try {
      const formData = await new Promise((resolve, reject) => {
        try {
          const formData = new FormData();
          uploadedFiles.forEach((file) => {
            const key = file.documentType;

            if (key && file.file) {
              formData.append(key, file.file);
            }
          });
          resolve(formData);
        } catch (error) {
          reject(error);
        }
      });

      switch (cart.from) {
        case "crimRecord":
          await dispatch(
            uploadCrimRecordDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;

        case "certificateNationality":
          await dispatch(
            uploadCertificateNationalityDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;

        case "certificateSingleness":
          await dispatch(
            uploadCertificateSinglenessDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;
        case "eneo":
          await dispatch(
            eneoDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;
        case "customsRebate":
          await dispatch(
            uploadCustomsRebateDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;
        case "businessPlan":
          await dispatch(
            uploadBusinessPlanDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;
        case "importation":
          await dispatch(
            uploadImportationDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;
        case "oapi":
          await dispatch(
            uploadOapiDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;

        case "trademark":
          await dispatch(
            uploadTrademarkDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;
        case "taxRegistration":
          await dispatch(
            uploadTaxRegistrationDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;
        case "cnpsRegistration":
          await dispatch(
            uploadCnpsRegistrationDocuments(
              user.token,
              formalityRequest.reference,
              formData,
              handleCallback,
            ),
          );
          break;

        default:
          break;
      }
    } catch (error) {
      console.error("Erreur lors de la création de FormData :", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      navigate("/formality-payment");
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }
    setSubmitting(false);
  };

  const isAllDocumentsUploaded =
    uploadedFiles.length === requiredDocuments.length &&
    requiredDocuments.every((doc) =>
      uploadedFiles.some((file) => file.documentType === doc),
    );

  return (
    <>
      <FormalityHeader from={cart.from} />
      <Loader isLoading={submitting} />
      <div className="legacy-flow docs-page">
        <div
          className="container mt-3 px-3 text-center"
          style={{ maxWidth: "700px" }}
        >
          <h4 className="text-center mb-4">
            Ajoutez les <span style={{ color: "#FF5500" }}>documents</span>{" "}
            nécessaires pour votre démarche
          </h4>
          <p className="text-muted">
            Vous n’êtes pas en possession de tous vos documents ? Cliquez ici
            pour les fournir plus tard à nos opérateurs via WhatsApp :{" "}
            <Link to={"/formality-payment"}>
              Fournir plus tard via WhatsApp{" "}
            </Link>
          </p>
        </div>

        <div
          className="container mt-4 text-center"
          style={{ maxWidth: "620px" }}
        >
          <div
            {...getRootProps()}
            className={`doc-dropzone ${isDragActive ? "is-active" : ""}`}
          >
            <input {...getInputProps()} />
            <MdOutlineUploadFile size={40} color="gray" />
            <p style={{ fontSize: "16px", color: "#333" }}>
              {isDragActive ? (
                "Déposez votre fichier ici..."
              ) : (
                <>
                  Glissez-déposez ou cliquez ici pour ajouter le fichier
                  contenant :
                  <span
                    style={{
                      color: "#FF5500",
                      fontWeight: "500",
                      transition: "background-color 0.5s ease",
                      backgroundColor: animate ? "#FFF3CD" : "transparent",
                      padding: "5px",
                      borderRadius: "5px",
                    }}
                  >
                    {documentTypes[currentDocument] || (
                      <span
                        style={{
                          color: "green",
                          fontWeight: "500",
                          transition: "background-color 0.5s ease",
                          backgroundColor: animate ? "#D4EDDA" : "transparent",
                          padding: "5px",
                          borderRadius: "5px",
                        }}
                      >
                        {isAllDocumentsUploaded
                          ? 'Tous les documents sont ajoutés ! Appuyez sur le "bouton" Continuer ci-dessous pour poursuivre.'
                          : ""}
                      </span>
                    )}
                  </span>
                </>
              )}
            </p>

            <p style={{ color: "#777", fontSize: "14px" }}>
              Formats acceptés : <LiaFilePdf size={20} />{" "}
              {acceptedLabel[currentDocument] || "PDF uniquement"}
            </p>
          </div>

          {/* Liste des fichiers téléchargés */}
          {uploadedFiles.length > 0 ? (
            <div className="mt-4">
              <h6>Fichiers ajoutés :</h6>
              <ListGroup>
                {uploadedFiles.map((file) => (
                  <ListGroup.Item
                    key={file.id}
                    className="d-flex align-items-center justify-content-between"
                  >
                    <div className="d-flex align-items-center">
                      <FiFile style={{ marginRight: "5px" }} size={20} />
                      {file.name}
                    </div>
                    <FaRegTimesCircle
                      onClick={() => removeFile(file.id, file.documentType)}
                      style={{
                        cursor: "pointer",
                        fontSize: "20px",
                        color: "red",
                      }}
                    />
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          ) : (
            <Alert className="mt-4" variant="info">
              Aucun fichier ajouté pour le moment.
            </Alert>
          )}

          {/* Bouton pour continuer */}
          <div className="d-flex justify-content-center mt-4">
            <Button
              disabled={!isAllDocumentsUploaded}
              className="flow-submit"
              onClick={handleButtonClick}
            >
              Continuer
            </Button>

            <div className="container mt-3 px-3 text-center">
              <Link to={"/formality-payment"}>
                Fournir plus tard via WhatsApp{" "}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <GuideSection {...documentsGuide} />
    </>
  );
}

export default FormalityDocuments;
